import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';
import {
  Account,
  Contract,
  ContractReportConfig,
  ContractSummaryReport,
  CreateAccountRequest,
  CreateContractRequest,
  CreateFundsMovementRequest,
  CreateTransactionRequest,
  FundsMovement,
  Invoice,
  ReclassifyFundsMovementRequest,
  SettlementLine,
  SettlementBatch,
  UpdateAccountRequest,
  UpdateContractRequest,
  BankReconciliationImport,
  BankReconciliationRow,
  BankReconciliationState,
} from '../ledger-client/types';

export const resolveAccountId = (value: unknown): string => {
  if (value && typeof value === 'object') {
    const id = (value as any).id;
    return String(id || '').trim();
  }
  return String(value || '').trim();
};

@Injectable({
  providedIn: 'root',
})
export class FinanceLedgerService {
  constructor(private restangular: Restangular) {}

  listAccountTypes(): Promise<Array<{ code: string; name: string; normalBalance: string; displayOrder: number }>> {
    return this.accounting().one('account-types').getList().toPromise() as Promise<Array<{ code: string; name: string; normalBalance: string; displayOrder: number }>>;
  }

  createAccount(payload: CreateAccountRequest): Promise<Account> {
    return this.accounting().one('accounts').customPOST(payload).toPromise() as Promise<Account>;
  }

  listContracts(query: {
    ownerUserId?: string;
    status?: string;
    contractType?: string;
    counterpartyType?: string;
    counterpartyId?: string;
    fromDate?: string;
    toDate?: string;
  } = {}): Promise<Contract[]> {
    return this.platform().all('contracts').getList(this.cleanQueryParams(query)).toPromise() as Promise<Contract[]>;
  }

  getContract(contractId: string): Promise<Contract> {
    return this.platform().one('contracts', contractId).get().toPromise() as Promise<Contract>;
  }

  createContract(payload: CreateContractRequest): Promise<Contract> {
    return this.platform().one('contracts').customPOST(payload).toPromise() as Promise<Contract>;
  }

  async safeUpdateContract(contractId: string, patch: UpdateContractRequest): Promise<Contract> {
    const latest = await this.getContract(contractId);
    try {
      return await this.updateContract(contractId, {
        ...patch,
        expectedUpdatedAt: latest.updatedAt,
      });
    } catch (error) {
      if (!this.isConflictError(error)) {
        throw error;
      }

      const refreshed = await this.getContract(contractId);
      return this.updateContract(contractId, {
        ...patch,
        expectedUpdatedAt: refreshed.updatedAt,
      });
    }
  }

  listFundsMovements(limit = 200, offset = 0, search = ''): Promise<FundsMovement[]> {
    return this.platform().one('funds-movements')
      .getList(this.cleanQueryParams({limit, offset, search}))
      .toPromise() as Promise<FundsMovement[]>;
  }

  listUnmappedFundsMovements(): Promise<FundsMovement[]> {
    return this.platform().one('reports').one('funds-movements').one('unmapped').getList().toPromise() as Promise<FundsMovement[]>;
  }

  reclassifyAndPostFundsMovement(movementId: string, payload: ReclassifyFundsMovementRequest): Promise<FundsMovement> {
    return this.platform()
      .one('funds-movements', movementId)
      .one('reclassify-and-post')
      .customPOST(payload)
      .toPromise() as Promise<FundsMovement>;
  }

  async ingestFundsMovement(payload: CreateFundsMovementRequest & { idempotencyKey?: string }): Promise<FundsMovement> {
    const idempotencyKey = payload.idempotencyKey || payload.externalId || this.generateIdempotencyKey();

    const movement = await this.platform().one('funds-movements').customPOST({
      ...payload,
      externalId: payload.externalId || idempotencyKey,
      autoPostToLedger: false,
      metadata: {
        ...(payload.metadata || {}),
        idempotencyKey,
      },
    }).toPromise() as FundsMovement;

    try {
      const postResult = await this.platform()
        .one('funds-movements', movement.id)
        .one('post-ledger')
        .customPOST()
        .toPromise() as { movementId: string; ledgerTransactionId: string; wasAlreadyPosted: boolean };

      return {
        ...movement,
        status: 'POSTED',
        ledgerTransactionId: postResult.ledgerTransactionId,
      };
    } catch (error) {
      if (!this.isConflictError(error)) {
        throw error;
      }

      return this.platform().one('funds-movements', movement.id).get().toPromise() as Promise<FundsMovement>;
    }
  }

  listSettlements(): Promise<SettlementBatch[]> {
    return this.reportingSettlement().one('settlements').getList().toPromise() as Promise<SettlementBatch[]>;
  }

  getSettlement(settlementBatchId: string): Promise<SettlementBatch & { lines: SettlementLine[] }> {
    return this.reportingSettlement().one('settlements', settlementBatchId).get().toPromise() as Promise<SettlementBatch & { lines: SettlementLine[] }>;
  }

  async runSettlementLifecycle(input: { contractId: string; periodStart: string; periodEnd: string; invoiceDueDate?: string }): Promise<{
    settlement: SettlementBatch;
    posted: SettlementBatch;
    paid?: SettlementBatch;
    invoiceId?: string;
  }> {
    const settlement = await this.reportingSettlement()
      .one('settlements')
      .one('run')
      .customPOST({
        contractId: input.contractId,
        periodStart: input.periodStart,
        periodEnd: input.periodEnd,
      })
      .toPromise() as SettlementBatch;

    const posted = await this.postSettlement(settlement.id);

    let invoiceId: string | undefined;
    if (input.invoiceDueDate) {
      const invoice = await this.issueInvoice(settlement.id, input.invoiceDueDate);
      invoiceId = invoice.id;
    }

    return {
      settlement,
      posted,
      invoiceId,
    };
  }

  postSettlement(settlementBatchId: string): Promise<SettlementBatch> {
    return this.reportingSettlement().one('settlements', settlementBatchId).one('post').customPOST().toPromise() as Promise<SettlementBatch>;
  }

  markSettlementPaid(settlementBatchId: string): Promise<SettlementBatch> {
    return this.reportingSettlement().one('settlements', settlementBatchId).one('pay').customPOST().toPromise() as Promise<SettlementBatch>;
  }

  issueInvoice(settlementBatchId: string, dueDate: string): Promise<Invoice> {
    return this.reportingSettlement().one('settlements', settlementBatchId).one('invoices').customPOST({ dueDate }).toPromise() as Promise<Invoice>;
  }

  listInvoices(): Promise<Invoice[]> {
    return this.reportingSettlement().one('invoices').getList().toPromise() as Promise<Invoice[]>;
  }

  createBankReconciliationImport(formData: FormData): Promise<{ item: BankReconciliationImport; wasExisting: boolean }> {
    return this.platform().one('bank-reconciliation').one('imports')
      .customPOST(formData, undefined, undefined, {'Content-Type': undefined})
      .toPromise() as Promise<{ item: BankReconciliationImport; wasExisting: boolean }>;
  }

  listBankReconciliationImports(limit = 100, offset = 0): Promise<BankReconciliationImport[]> {
    return this.platform().one('bank-reconciliation').all('imports')
      .getList({limit, offset}).toPromise() as Promise<BankReconciliationImport[]>;
  }

  getBankReconciliationImport(importId: string): Promise<BankReconciliationImport> {
    return this.platform().one('bank-reconciliation').one('imports', importId)
      .get().toPromise() as Promise<BankReconciliationImport>;
  }

  deleteBankReconciliationImport(importId: string): Promise<void> {
    return this.platform().one('bank-reconciliation').one('imports', importId)
      .customDELETE().toPromise() as Promise<void>;
  }

  listBankReconciliationRows(
    importId: string,
    state?: BankReconciliationState,
    limit = 100,
    offset = 0,
    search = '',
    sortBy = 'rowNumber',
    sortDirection: 'asc' | 'desc' = 'asc',
  ): Promise<BankReconciliationRow[]> {
    return this.platform().one('bank-reconciliation').one('imports', importId).all('rows')
      .getList(this.cleanQueryParams({state, limit, offset, search: search.trim(), sortBy, sortDirection}))
      .toPromise() as Promise<BankReconciliationRow[]>;
  }

  getContractSummary(
    contractId: string,
    periodStart: string,
    periodEnd: string,
    query: Record<string, unknown> = {},
  ): Promise<ContractSummaryReport> {
    return this.platform()
      .one('reports')
      .one('contracts', contractId)
      .one('summary')
      .get(this.cleanQueryParams({ periodStart, periodEnd, ...query }))
      .toPromise() as Promise<ContractSummaryReport>;
  }

  getContractReportConfig(contractId: string): Promise<ContractReportConfig> {
    return this.platform().one('contracts', contractId).one('report-config').get().toPromise() as Promise<ContractReportConfig>;
  }

  listAccounts(status?: 'active' | 'archived'): Promise<Account[]> {
    const listRequest = (query?: Record<string, string>): Promise<Account[]> => {
      return this.accounting()
        .all('accounts')
        .getList(query)
        .toPromise() as Promise<Account[]>;
    };

    if (!status) {
      return listRequest();
    }

    return listRequest(this.cleanQueryParams({ status })).catch((error: unknown) => {
      const httpStatus = (error as any)?.status ?? (error as any)?.originalError?.status;
      if (httpStatus !== 404) {
        throw error;
      }

      // Backward compatibility: older backends may not support status query params.
      return listRequest().then((accounts) => accounts.filter((account) => account.status === status));
    });
  }

  getAccount(accountId: string): Promise<Account> {
    const resolvedAccountId = resolveAccountId(accountId);
    return this.accounting().one('accounts', resolvedAccountId).get().toPromise() as Promise<Account>;
  }

  updateAccount(accountId: string, patch: UpdateAccountRequest): Promise<Account> {
    const resolvedAccountId = resolveAccountId(accountId);
    return this.accounting().one('accounts', resolvedAccountId).customPATCH(patch).toPromise() as Promise<Account>;
  }

  async safeUpdateAccount(accountId: string, patch: UpdateAccountRequest): Promise<Account> {
    const resolvedAccountId = resolveAccountId(accountId);
    const latest = await this.getAccount(resolvedAccountId);
    try {
      return await this.updateAccount(resolvedAccountId, {
        ...patch,
        expectedUpdatedAt: latest.updatedAt,
      });
    } catch (error) {
      if (!this.isConflictError(error)) {
        throw error;
      }

      const refreshed = await this.getAccount(resolvedAccountId);
      return this.updateAccount(resolvedAccountId, {
        ...patch,
        expectedUpdatedAt: refreshed.updatedAt,
      });
    }
  }

  updateAccountStatus(accountId: string, status: 'active' | 'archived'): Promise<Account> {
    const resolvedAccountId = resolveAccountId(accountId);
    return this.safeUpdateAccount(resolvedAccountId, { status });
  }

  listTransactions(): Promise<unknown[]> {
    return this.accounting().one('transactions').getList().toPromise() as Promise<unknown[]>;
  }

  createTransaction(payload: CreateTransactionRequest): Promise<unknown> {
    return this.accounting().one('transactions').customPOST(payload).toPromise();
  }

  getTrialBalance(asOfDate?: string): Promise<unknown> {
    return this.accounting().one('ledger').one('trial-balance').get({ asOfDate }).toPromise();
  }

  getGeneralLedger(accountId?: string, fromDate?: string, toDate?: string): Promise<unknown> {
    return this.accounting()
      .one('ledger')
      .customGET('general-ledger', this.cleanQueryParams({ accountId, fromDate, toDate }))
      .toPromise();
  }

  isConflictError(error: unknown): boolean {
    const status = (error as any)?.status ?? (error as any)?.originalError?.status;
    return status === 409;
  }

  getErrorMessage(error: unknown, fallbackMessage: string): string {
    const status = (error as any)?.status ?? (error as any)?.originalError?.status;
    const data = (error as any)?.data ?? (error as any)?.originalError?.data;
    const apiMessage = data?.error || data?.message || (error as any)?.message;

    if (this.isAuthorizationError(error)) {
      return 'Access denied. You do not have permission to access this finance resource.';
    }

    if (status && apiMessage) {
      return `${fallbackMessage} (${status}): ${apiMessage}`;
    }

    if (apiMessage) {
      return `${fallbackMessage}: ${apiMessage}`;
    }

    return fallbackMessage;
  }

  isAuthorizationError(error: unknown): boolean {
    const status = (error as any)?.status ?? (error as any)?.originalError?.status;
    return status === 401 || status === 403;
  }

  generateIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  private platform() {
    return this.restangular.one('admin').one('ledger');
  }

  private accounting() {
    return this.platform().one('accounting');
  }

  private reportingSettlement() {
    return this.platform().one('reporting-settlement');
  }

  private updateContract(contractId: string, patch: UpdateContractRequest): Promise<Contract> {
    return this.platform().one('contracts', contractId).customPATCH(patch).toPromise() as Promise<Contract>;
  }

  private cleanQueryParams(params: Record<string, unknown>): Record<string, string> {
    const cleaned: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = String(value);
      }
    });
    return cleaned;
  }

}
